import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Award, Users, Clock, Calendar, CheckCircle, Target, BookOpen, Briefcase, Building, Play, Phone, Mail, MapPin, MessageSquare, GraduationCap, Laptop, TrendingUp, FileText, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Chatbot } from '@/components/Chatbot';
import { trackCourseView, trackBookNowClicked, trackCheckoutInitiated } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Course slug to ID mapping
const COURSE_SLUGS = {
  'aws-cloud-practitioner': 'aws-cloud-practitioner',
  'aws-solutions-architect-associate': 'aws-solutions-architect',
  'comptia-a-plus-core-1': 'comptia-a-plus-core1',
  'comptia-a-plus-core-2': 'comptia-a-plus-core2',
  'comptia-security-plus': 'comptia-security-plus',
  'cisco-ccna': 'cisco-ccna',
  'cisco-ccst': 'cisco-ccst',
  'microsoft-azure': 'azure-foundations'
};

const LEVEL_CONFIG = {
  beginner: { color: 'bg-green-500', label: 'Beginner Friendly', textColor: 'text-green-400' },
  intermediate: { color: 'bg-yellow-500', label: 'Intermediate', textColor: 'text-yellow-400' },
  advanced: { color: 'bg-red-500', label: 'Advanced', textColor: 'text-red-400' }
};

// Course images mapping
const COURSE_IMAGES = {
  'aws-cloud-practitioner': 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800',
  'aws-solutions-architect': 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800',
  'comptia-a-plus-core1': 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800',
  'comptia-a-plus-core2': 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800',
  'comptia-security-plus': 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800',
  'cisco-ccna': 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800',
  'cisco-ccst': 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800',
  'azure-foundations': 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=800'
};

// Exam information for each course
const EXAM_INFO = {
  'aws-cloud-practitioner': {
    name: 'AWS Certified Cloud Practitioner (CLF-C02)',
    duration: '90 minutes',
    questions: '65 questions',
    passingScore: '700/1000 (70%)',
    format: 'Multiple choice and multiple response'
  },
  'aws-solutions-architect': {
    name: 'AWS Certified Solutions Architect – Associate (SAA-C03)',
    duration: '130 minutes',
    questions: '65 questions',
    passingScore: '720/1000 (72%)',
    format: 'Multiple choice and multiple response'
  },
  'comptia-a-plus-core1': {
    name: 'CompTIA A+ Core 1 (220-1101)',
    duration: '90 minutes',
    questions: '90 questions',
    passingScore: '675/900 (75%)',
    format: 'Multiple choice and performance-based questions'
  },
  'comptia-a-plus-core2': {
    name: 'CompTIA A+ Core 2 (220-1102)',
    duration: '90 minutes',
    questions: '90 questions',
    passingScore: '700/900 (78%)',
    format: 'Multiple choice and performance-based questions'
  },
  'comptia-security-plus': {
    name: 'CompTIA Security+ (SY0-701)',
    duration: '90 minutes',
    questions: '90 questions',
    passingScore: '750/900 (83%)',
    format: 'Multiple choice and performance-based questions'
  },
  'cisco-ccna': {
    name: 'Cisco CCNA (200-301)',
    duration: '120 minutes',
    questions: '100-120 questions',
    passingScore: '825/1000 (82.5%)',
    format: 'Multiple choice, drag-and-drop, simulation'
  },
  'cisco-ccst': {
    name: 'Cisco Certified Support Technician (CCST)',
    duration: '50 minutes',
    questions: '40-50 questions',
    passingScore: '800/1000 (80%)',
    format: 'Multiple choice'
  },
  'azure-foundations': {
    name: 'Microsoft Azure Fundamentals (AZ-900)',
    duration: '65 minutes',
    questions: '40-60 questions',
    passingScore: '700/1000 (70%)',
    format: 'Multiple choice'
  }
};

// Certifications included for each course
const CERTIFICATIONS_INCLUDED = {
  'aws-cloud-practitioner': ['AWS Certified Cloud Practitioner'],
  'aws-solutions-architect': ['AWS Certified Solutions Architect – Associate'],
  'comptia-a-plus-core1': ['CompTIA A+ Core 1'],
  'comptia-a-plus-core2': ['CompTIA A+ Core 2', 'Full CompTIA A+ (combined with Core 1)'],
  'comptia-security-plus': ['CompTIA Security+'],
  'cisco-ccna': ['Cisco CCNA'],
  'cisco-ccst': ['Cisco CCST Networking'],
  'azure-foundations': ['Microsoft Azure Fundamentals (AZ-900)']
};

// Whats included for all courses
const WHATS_INCLUDED = [
  'Instructor-led classroom training',
  'Maximum 6 students per class',
  'Official course materials',
  'Hands-on labs with real equipment',
  'Exam voucher (with exam bundle)',
  'Pearson VUE onsite testing',
  'WhatsApp support after course',
  'Certificate of completion'
];

export const CoursePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [examBundle, setExamBundle] = useState(false);
  const [studentInfo, setStudentInfo] = useState({ name: '', email: '', phone: '' });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const courseId = COURSE_SLUGS[slug];
    if (courseId) {
      fetchCourse(courseId);
      fetchFaqs(courseId);
    } else {
      navigate('/');
    }
  }, [slug, navigate]);

  const fetchCourse = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses`);
      const foundCourse = response.data.find(c => c.id === courseId);
      if (foundCourse) {
        setCourse(foundCourse);
        trackCourseView(foundCourse.name, foundCourse.id);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to fetch course:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFaqs = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}/faqs`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    }
  };

  const handleBookNow = async () => {
    trackBookNowClicked(course.name, course.id);
    setShowBooking(true);
    setBookingStep(1);
    try {
      const response = await axios.get(`${API}/course-dates/${course.id}`);
      setAvailableDates(response.data);
    } catch (error) {
      toast.error('Failed to load available dates');
    }
  };

  const handleDateSelect = () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }
    setBookingStep(2);
  };

  const handleExamChoice = () => {
    setBookingStep(3);
  };

  const handleBookingSubmit = async () => {
    if (!studentInfo.name || !studentInfo.email || !studentInfo.phone) {
      toast.error('Please fill in all your details');
      return;
    }

    setBookingLoading(true);
    trackCheckoutInitiated(course.name, examBundle ? course.price_with_exam : course.price_without_exam);
    
    try {
      const bookingResponse = await axios.post(`${API}/bookings`, {
        student_name: studentInfo.name,
        student_email: studentInfo.email,
        student_phone: studentInfo.phone,
        course_id: course.id,
        date: selectedDate,
        exam_bundle: examBundle
      });

      const booking = bookingResponse.data;
      const checkoutResponse = await axios.post(`${API}/checkout/session`, {
        booking_id: booking.id,
        origin_url: window.location.origin
      });

      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      toast.error('Booking failed. Please try again.');
      setBookingLoading(false);
    }
  };

  const scrollToContact = () => {
    navigate('/#contact');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  if (!course) return null;

  const levelConfig = LEVEL_CONFIG[course.level] || LEVEL_CONFIG.beginner;
  const courseImage = COURSE_IMAGES[course.id] || COURSE_IMAGES['aws-cloud-practitioner'];
  const examInfo = EXAM_INFO[course.id];
  const certifications = CERTIFICATIONS_INCLUDED[course.id] || [];
  const metaTitle = `${course.name} Certification Training London | Train Secure`;
  const metaDescription = `${course.name} bootcamp in London. ${course.duration_days} days intensive training. From £${course.price_without_exam}. Exam included option available. Pearson VUE Test Centre. Max 6 students.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${course.name}, ${course.name} certification, ${course.name} training London, ${course.name} bootcamp UK`} />
        <link rel="canonical" href={`https://www.trainsecure.co.uk/courses/${slug}`} />
        
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://www.trainsecure.co.uk/courses/${slug}`} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={courseImage} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": course.name,
            "description": course.description,
            "provider": {
              "@type": "Organization",
              "name": "Train Secure - North London College",
              "url": "https://www.trainsecure.co.uk"
            },
            "offers": {
              "@type": "Offer",
              "price": course.price_without_exam,
              "priceCurrency": "GBP",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
        
        {faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-[#0a0e1a] text-white">
        {/* Header */}
        <header className="border-b border-gray-800 bg-[#0a0e1a]/95 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_nlc-bootcamp/artifacts/ipcogjr5_NLC-%20TRAIN%20SECURE%20LOGO.jpg"
                alt="Train Secure"
                className="h-10 w-10 rounded-full object-cover"
              />
              <div>
                <h1 className="text-base md:text-lg font-bold">Train Secure</h1>
                <p className="text-xs text-cyan-400">North London College</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/#contact')} size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold">
                Get in Touch
              </Button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2 hover:bg-slate-800 rounded-lg"
                aria-label="Toggle menu"
                data-testid="menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          
          {/* Menu Dropdown - Same for desktop and mobile */}
          {mobileMenuOpen && (
            <div className="bg-[#0f1420] border-t border-gray-800 px-4 py-4 space-y-3">
              <a href="/#courses" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Courses</a>
              <a href="/#testimonials" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Reviews</a>
              <a href="/#contact" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Contact</a>
              <a href="/blog" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Blog</a>
              <a href="https://wa.me/447435177049" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-400 py-2">
                <MessageSquare className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          )}
        </header>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <a href="/" className="hover:text-cyan-400">Home</a>
            <span>/</span>
            <a href="/#courses" className="hover:text-cyan-400">Courses</a>
            <span>/</span>
            <span className="text-white">{course.name}</span>
          </div>
        </div>

        {/* Hero Section with Image */}
        <section className="py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Left - Course Image and Info */}
              <div>
                <div className="relative rounded-2xl overflow-hidden mb-6">
                  <img 
                    src={courseImage} 
                    alt={course.name}
                    className="w-full h-64 md:h-80 object-cover"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className={`${levelConfig.color} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                      {levelConfig.label}
                    </span>
                    {course.most_popular && (
                      <span className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    )}
                  </div>
                </div>
                
                <h1 className="text-3xl md:text-4xl font-bold mb-4">{course.name}</h1>
                <p className="text-lg text-gray-300 mb-6">{course.description}</p>
                
                <div className="flex flex-wrap gap-4 mb-6 text-gray-300">
                  <div className="flex items-center gap-2 bg-[#111827] px-4 py-2 rounded-lg">
                    <Calendar className="w-5 h-5 text-cyan-400" />
                    <span>{course.duration_days} Days</span>
                  </div>
                  <div className="flex items-center gap-2 bg-[#111827] px-4 py-2 rounded-lg">
                    <Clock className="w-5 h-5 text-cyan-400" />
                    <span>9:30 AM - 4:30 PM</span>
                  </div>
                  <div className="flex items-center gap-2 bg-[#111827] px-4 py-2 rounded-lg">
                    <Users className="w-5 h-5 text-cyan-400" />
                    <span>Max 6 Students</span>
                  </div>
                </div>
              </div>

              {/* Right - Booking Card */}
              <div className="bg-[#111827] rounded-2xl p-6 border border-gray-800">
                <h3 className="text-xl font-bold mb-4">Course Pricing</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center p-4 bg-[#0a0e1a] rounded-lg">
                    <span className="text-gray-300">Without exam:</span>
                    <span className="text-2xl font-bold">£{course.price_without_exam}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                    <div>
                      <span className="text-white font-medium">With exam:</span>
                      <p className="text-sm text-cyan-400">Exam voucher included</p>
                    </div>
                    <span className="text-2xl font-bold text-cyan-400">£{course.price_with_exam}</span>
                  </div>
                </div>

                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Only £50 deposit to book</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Pay in 3 with Klarna</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Pearson VUE Test Centre</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Certificate on completion</span>
                  </div>
                </div>

                <Button onClick={handleBookNow} className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-900 text-lg py-6 mb-3 font-semibold">
                  Book Now - £50 Deposit
                </Button>
                
                <a href="https://wa.me/447435177049" target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="w-full border-green-500 text-green-400 hover:bg-green-500/10">
                    <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Course Details Grid - with colored overlays */}
        <section className="py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Who This Is For - PURPLE OVERLAY */}
              <Card className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border-purple-500/30 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center">
                      <Target className="w-5 h-5 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Who This Course Is For</h3>
                  </div>
                  <ul className="space-y-3">
                    {(course.who_this_is_for && course.who_this_is_for.length > 0) ? (
                      course.who_this_is_for.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))
                    ) : (
                      <>
                        <li className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span>Career changers looking to enter IT</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span>Recent graduates seeking industry certifications</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span>IT professionals looking to validate their skills</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span>Anyone wanting to boost their earning potential</span>
                        </li>
                      </>
                    )}
                  </ul>
                </CardContent>
              </Card>

              {/* Career Outcomes - GREEN OVERLAY */}
              <Card className="bg-gradient-to-br from-green-900/40 to-emerald-800/20 border-green-500/30 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-green-500/30 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Career Outcomes</h3>
                  </div>
                  <ul className="space-y-3">
                    {(course.career_outcomes && course.career_outcomes.length > 0) ? (
                      course.career_outcomes.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))
                    ) : (
                      <>
                        <li className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Qualify for higher-paying IT roles</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Stand out to employers with industry certification</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Open doors to specialized IT career paths</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-200">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>Increase your earning potential by 20-40%</span>
                        </li>
                      </>
                    )}
                  </ul>
                </CardContent>
              </Card>

              {/* Certifications Included - YELLOW OVERLAY */}
              <Card className="bg-gradient-to-br from-yellow-900/40 to-amber-800/20 border-yellow-500/30 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-yellow-500/30 flex items-center justify-center">
                      <Award className="w-5 h-5 text-yellow-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Certifications Included</h3>
                  </div>
                  <div className="space-y-3">
                    {certifications.map((cert, i) => (
                      <div key={i} className="flex items-center gap-3 bg-yellow-500/10 p-3 rounded-lg border border-yellow-500/20">
                        <Award className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                        <span className="text-yellow-100 font-medium">{cert}</span>
                      </div>
                    ))}
                    <p className="text-sm text-yellow-200/70 mt-2">
                      Exam voucher included with the "With Exam" bundle. Take your exam at our Pearson VUE Test Centre.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Exam Information - BLUE OVERLAY */}
              {examInfo && (
                <Card className="bg-gradient-to-br from-blue-900/40 to-indigo-800/20 border-blue-500/30 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-blue-500/30 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Exam Information</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                        <p className="text-blue-100 font-semibold text-lg mb-3">{examInfo.name}</p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-blue-300">Duration:</span>
                            <p className="text-white font-medium">{examInfo.duration}</p>
                          </div>
                          <div>
                            <span className="text-blue-300">Questions:</span>
                            <p className="text-white font-medium">{examInfo.questions}</p>
                          </div>
                          <div>
                            <span className="text-blue-300">Passing Score:</span>
                            <p className="text-white font-medium">{examInfo.passingScore}</p>
                          </div>
                          <div>
                            <span className="text-blue-300">Format:</span>
                            <p className="text-white font-medium">{examInfo.format}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Why Choose Train Secure - CYAN OVERLAY */}
              <Card className="bg-gradient-to-br from-cyan-900/40 to-teal-800/20 border-cyan-500/30 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/30 flex items-center justify-center">
                      <Building className="w-5 h-5 text-cyan-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Why Choose Train Secure</h3>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-gray-200">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Pearson VUE Authorized Test Centre - take exams onsite</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Maximum 6 students per class for personalized attention</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Industry-experienced certified instructors</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Flexible payment with Klarna (Pay in 3)</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-200">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>WhatsApp support during and after your course</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* What's Included - SLATE/NEUTRAL */}
              <Card className="bg-[#111827] border-gray-700 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-gray-300" />
                    </div>
                    <h3 className="text-xl font-bold text-white">What's Included</h3>
                  </div>
                  <ul className="space-y-3">
                    {WHATS_INCLUDED.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-300">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>

        {/* YouTube Videos */}
        {course.youtube_videos && course.youtube_videos.length > 0 && (
          <section className="py-8 px-4 bg-[#111827]">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-bold mb-6">Learn More About This Certification</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {course.youtube_videos.map((video, index) => (
                  <div key={index} className="rounded-xl overflow-hidden bg-[#0a0e1a] border border-gray-800">
                    <div className="aspect-video">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.video_id}`}
                        title={video.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-white">{video.title}</h4>
                      <p className="text-sm text-gray-400 mt-1">{video.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQs */}
        {faqs.length > 0 && (
          <section className="py-8 px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 text-center">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-[#111827] rounded-xl p-5 border border-gray-800">
                    <h3 className="text-lg font-semibold text-white mb-2">{faq.question}</h3>
                    <p className="text-gray-300">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Location Map */}
        <section className="py-8 px-4 bg-[#111827]">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Our Location</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl overflow-hidden h-64 md:h-80">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2479.5!2d-0.0685!3d51.5985!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761fc9e1a5e8fd%3A0x8f9d6d5e5c0e5a5a!2s376%20High%20Rd%2C%20London%20N17%209HY!5e0!3m2!1sen!2suk!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Train Secure Location"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h3 className="text-xl font-bold mb-4">Train Secure - North London College</h3>
                <div className="space-y-3 text-gray-300">
                  <p className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                    <span>376 High Road, Tottenham, London N17 9HY</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-cyan-400" />
                    <span>0208 808 8008</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-cyan-400" />
                    <span>info@trainsecure.co.uk</span>
                  </p>
                  <a href="https://wa.me/447435177049" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-green-400 hover:text-green-300">
                    <MessageSquare className="w-5 h-5" />
                    <span>WhatsApp: +44 7435 177049</span>
                  </a>
                </div>
                <p className="mt-4 text-sm text-gray-400">
                  Easily accessible by public transport. Nearest stations: Tottenham Hale (Victoria Line) and Bruce Grove (Overground).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Start Your {course.name} Journey?</h2>
            <p className="text-lg text-gray-300 mb-6">Book your place today with just a £50 deposit. Limited to 6 students per class.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={handleBookNow} size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 text-lg px-8 font-semibold">
                Book Now - £50 Deposit
              </Button>
              <a href="https://wa.me/447435177049" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg" className="border-green-500 text-green-400 hover:bg-green-500/10 text-lg px-8 w-full sm:w-auto">
                  <MessageSquare className="w-5 h-5 mr-2" /> WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-gray-800 py-10 px-4 bg-[#0a0e1a]">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <h4 className="font-bold mb-4">Train Secure</h4>
                <p className="text-gray-400 text-sm">North London College - Pearson VUE Test Centre. Industry-leading IT certification training in London.</p>
              </div>
              <div>
                <h4 className="font-bold mb-4">Contact</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> 0208 808 8008</li>
                  <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> info@trainsecure.co.uk</li>
                  <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> +44 7435 177049</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4">Location</h4>
                <p className="text-gray-400 text-sm flex items-start gap-2">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-1" />
                  376 High Road, Tottenham, London N17 9HY
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-4">Courses</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li><a href="/courses/aws-cloud-practitioner" className="hover:text-cyan-400">AWS Cloud Practitioner</a></li>
                  <li><a href="/courses/comptia-security-plus" className="hover:text-cyan-400">CompTIA Security+</a></li>
                  <li><a href="/courses/cisco-ccna" className="hover:text-cyan-400">Cisco CCNA</a></li>
                  <li><a href="/blog" className="hover:text-cyan-400">Blog</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
              © 2025 Train Secure - North London College. All rights reserved.
            </div>
          </div>
        </footer>

        {/* Booking Dialog */}
        <Dialog open={showBooking} onOpenChange={setShowBooking}>
          <DialogContent className="bg-[#111827] border-gray-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {bookingStep === 1 && 'Select Your Date'}
                {bookingStep === 2 && 'Exam Bundle Option'}
                {bookingStep === 3 && 'Your Details'}
              </DialogTitle>
            </DialogHeader>
            
            {bookingStep === 1 && (
              <div className="space-y-4">
                <p className="text-gray-400">Choose your preferred start date for {course?.name}</p>
                <RadioGroup value={selectedDate} onValueChange={setSelectedDate}>
                  {availableDates
                    .filter(date => new Date(date.date) >= new Date()) // Filter out past dates
                    .map((date) => {
                      const d = new Date(date.date);
                      const formattedDate = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear().toString().slice(-2)}`;
                      return (
                        <div key={date.date} className="flex items-center space-x-3 p-3 rounded-lg bg-[#0a0e1a] border border-gray-700 hover:border-cyan-500/50 cursor-pointer">
                          <RadioGroupItem value={date.date} id={date.date} />
                          <Label htmlFor={date.date} className="flex-1 cursor-pointer">
                            <span className="font-medium">{formattedDate}</span>
                          </Label>
                        </div>
                      );
                    })}
                </RadioGroup>
                <Button onClick={handleDateSelect} className="w-full bg-cyan-500 hover:bg-cyan-600 py-5 text-slate-900 font-semibold">
                  Continue
                </Button>
              </div>
            )}

            {bookingStep === 2 && (
              <div className="space-y-4">
                <p className="text-gray-400">Would you like to include the exam voucher?</p>
                <RadioGroup value={examBundle ? 'yes' : 'no'} onValueChange={(v) => setExamBundle(v === 'yes')}>
                  <div className="p-4 rounded-lg bg-[#0a0e1a] border border-gray-700 hover:border-cyan-500/50 cursor-pointer">
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="no" id="no-exam" />
                      <Label htmlFor="no-exam" className="cursor-pointer flex-1">
                        <span className="font-medium">Training Only - £{course?.price_without_exam}</span>
                      </Label>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30 hover:border-cyan-500 cursor-pointer">
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="yes" id="with-exam" />
                      <Label htmlFor="with-exam" className="cursor-pointer flex-1">
                        <span className="font-medium text-cyan-400">With Exam Voucher - £{course?.price_with_exam}</span>
                        <p className="text-sm text-gray-400">Recommended - take your exam at our centre</p>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
                <Button onClick={handleExamChoice} className="w-full bg-cyan-500 hover:bg-cyan-600 py-5 text-slate-900 font-semibold">
                  Continue
                </Button>
              </div>
            )}

            {bookingStep === 3 && (
              <div className="space-y-4">
                <div>
                  <Label>Full Name</Label>
                  <Input 
                    value={studentInfo.name}
                    onChange={(e) => setStudentInfo({...studentInfo, name: e.target.value})}
                    className="bg-[#0a0e1a] border-gray-700 mt-1"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={studentInfo.email}
                    onChange={(e) => setStudentInfo({...studentInfo, email: e.target.value})}
                    className="bg-[#0a0e1a] border-gray-700 mt-1"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={studentInfo.phone}
                    onChange={(e) => setStudentInfo({...studentInfo, phone: e.target.value})}
                    className="bg-[#0a0e1a] border-gray-700 mt-1"
                    placeholder="07123 456789"
                  />
                </div>
                <div className="bg-[#0a0e1a] p-4 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-400">Course:</span>
                    <span className="text-white">{course?.name}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-400">Total:</span>
                    <span className="text-white">£{examBundle ? course?.price_with_exam : course?.price_without_exam}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-700 pt-2 mt-2">
                    <span className="text-gray-400">Deposit now:</span>
                    <span className="text-cyan-400 font-bold">£50</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Pay the remaining balance on or before your course start date.
                  </p>
                </div>
                <Button onClick={handleBookingSubmit} disabled={bookingLoading} className="w-full bg-cyan-500 hover:bg-cyan-600 py-5 text-slate-900 font-semibold">
                  {bookingLoading ? 'Processing...' : 'Pay £50 Deposit'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Chatbot />
      </div>
    </>
  );
};

export default CoursePage;
